import {
    CreateWidgetRequest,
    ICreateWidget,
    IGraphType,
    IKpi,
    IWidget,
    IWidgetType,
} from '../services/api/widget/types';
import {
    addDashboardWidget,
    getDashboardWidget,
    updateDashboardWidget,
} from '../services/api/dashboard/dashboardWidgetApi';
import { computed, ref, watch } from 'vue';
import {
    getDefaultWidgetDimensions,
    isGraphFormWidget,
    isGraphWidget,
    isTextFormWidget,
    isTextWidget,
} from '../utils/widgetUtils';

import { EchoFilterRequest, FilterStoreName } from '../services/api/filter/types';
import { ILibraryWidget } from '../services/api/library/types';
import { defineStore } from 'pinia';
import { getKpi } from '../services/api/widget/kpiApi';
import { useDashboardStore } from './dashboard';
import { useFilterStoreByName } from './filter';
import { widgetPreview } from '../services/api/widget/widgetApi';
import { useGrid } from './grid';

const defaultWidget: ICreateWidget<'Graph'> = {
    type: 'Graph',
    title: '',
    description: '',
    graphType: undefined,
    kpiVisualizationTypeId: '',
};

const createWidgetRequestFromFormData = (
    widgetData: ICreateWidget,
    originalWidgetData: IWidget | null,
    filter: EchoFilterRequest
): CreateWidgetRequest | null => {
    const newWidget: CreateWidgetRequest = {
        title: widgetData.title,
        widgetType: widgetData.type,
        description: widgetData.description,
        comment: originalWidgetData?.comment ?? null,
    };
    if (isGraphFormWidget(widgetData)) {
        (newWidget as CreateWidgetRequest<'Graph'>).graphWidget = {
            graphType: widgetData.graphType!,
            kpiVisualizationTypeId: widgetData.kpiVisualizationTypeId,
            filter,
        };
    } else if (isTextFormWidget(widgetData)) {
        (newWidget as CreateWidgetRequest<'Text'>).textWidget = {
            text: widgetData.text,
        };
    } else {
        return {
            ...originalWidgetData,
            ...newWidget,
        };
    }
    return newWidget;
};

const createWidgetFormDataFromDto = (widgetData: IWidget) => {
    let widgetFormData = {
        title: widgetData.title,
        description: widgetData.description ?? '',
        type: widgetData.widgetType,
    };
    if (isGraphWidget(widgetData)) {
        const graphMeta = widgetData.graphWidget ?? widgetData.graphWidgetWithoutData;
        (widgetFormData as ICreateWidget<'Graph'>).graphType = graphMeta?.graphType;

        (widgetFormData as ICreateWidget<'Graph'>).kpiVisualizationTypeId =
            graphMeta?.kpi.visualizationTypeId ?? '';
    } else if (isTextWidget(widgetData)) {
        (widgetFormData as ICreateWidget<'Text'>).text = widgetData.textWidget?.text ?? '';
    }
    return widgetFormData;
};

const getDefaultDimensionsFromFormData = (widgetData: ICreateWidget) => {
    const { h, w } = getDefaultWidgetDimensions(widgetData);
    return { height: h, width: w };
};

const getWidgetLayoutRequest = (dashboardId: string, widgetId: string) => {
    const gridStore = useGrid();
    const gridItem = gridStore.grid
        ?.getGridItems()
        ?.find((el: { gridstackNode?: { id?: string } }) => el.gridstackNode?.id === widgetId);

    if (gridItem?.gridstackNode) {
        const node = gridItem.gridstackNode;
        return {
            positionX: node.x ?? 0,
            positionY: node.y ?? 0,
            height: node.h ?? 0,
            width: node.w ?? 0,
        };
    }

    const dashboardStore = useDashboardStore();
    const layout = dashboardStore.dashboards[dashboardId]?.widgetLayout?.find(
        (item) => item.widgetId === widgetId
    );

    return {
        positionX: layout?.positionX,
        positionY: layout?.positionY,
        height: layout?.height,
        width: layout?.width,
    };
};

export type WidgetFormStore = ReturnType<typeof useWidgetFormStore>;

export const useWidgetFormStore = defineStore('widgetForm', () => {
    const isNewWidget = ref<boolean>(true);
    const widget = ref<ICreateWidget>(defaultWidget);
    const originalWidgetData = ref<IWidget | null>(null);
    const kpi = ref<IKpi[]>();
    const mode = ref<'create' | 'edit' | 'copyLibrary'>('create');
    // Set by a widget's "remove and choose again" right before it opens the form for editing.
    // Keyed by widget so a form that never opened cannot strip another widget's filter later.
    const removeInvalidLevelFilterForWidgetId = ref<string | null>(null);

    const kpiLoading = ref<boolean>(false);
    const widgetLoading = ref<boolean>(false);
    const submitLoading = ref<boolean>(false);

    const formLoading = computed(
        () => kpiLoading.value || widgetLoading.value || submitLoading.value
    );

    const formIsValid = computed(() => {
        if (isGraphFormWidget(widget.value) && !widget.value.kpiVisualizationTypeId) {
            return false;
        } else if (isTextFormWidget(widget.value) && !widget.value.text) {
            return false;
        }
        return true;
    });

    const filter = useFilterStoreByName(FilterStoreName.WidgetFilter);

    watch(
        () => filter.requestData,
        () => updateWidget('filter', filter.requestData),
        { deep: true }
    );

    const fetchKpiOtions = async function () {
        kpiLoading.value = true;
        try {
            const response = await getKpi();
            kpi.value = response.data.availableKpis;
            kpiLoading.value = false;
        } catch (error) {
            console.error('Error fetching KPI options:', error);
            kpiLoading.value = false;
            return;
        }
    };

    const setSelectedWidgetType = function (id: 'Graph' | 'Text') {
        widget.value.type = id;
    };

    const updateWidget = function (
        key: keyof ICreateWidget<'Graph'> | keyof ICreateWidget<'Text'>,
        value: string | IWidgetType | undefined | EchoFilterRequest
    ) {
        widget.value = { ...widget.value, [key]: value };
    };

    const fetchPreview = async function (
        visualizationTypeId: string,
        graphType: IGraphType,
        filter: EchoFilterRequest
    ) {
        const request = {
            kpiWidget: { visualizationTypeId },
            filter,
            graphType,
        };
        try {
            const response = await widgetPreview(request);
            return response.data;
        } catch (error) {
            console.error('Error fetching widget preview:', error);
            return null;
        }
    };

    const createNewWidget = async function (dashboardId: string) {
        submitLoading.value = true;
        let newWidget = createWidgetRequestFromFormData(
            widget.value,
            originalWidgetData.value,
            filter.requestData
        );
        if (!newWidget) {
            console.error('Invalid widget data');
            submitLoading.value = false;
            return;
        }
        try {
            const defaultDimensions = getDefaultDimensionsFromFormData(widget.value);
            await addDashboardWidget({ widget: newWidget, dashboardId, ...defaultDimensions });
            const dashboardStore = useDashboardStore();
            await dashboardStore.refreshSelectedDashboard(dashboardId);
            submitLoading.value = false;
        } catch (error) {
            submitLoading.value = false;
            console.error('Error creating new widget:', error);
        }
    };

    const editWidget = async function (dashboardId: string, widgetId: string) {
        submitLoading.value = true;
        const newWidget = createWidgetRequestFromFormData(
            widget.value,
            originalWidgetData.value,
            filter.requestData
        );
        const widgetLayout = getWidgetLayoutRequest(dashboardId, widgetId);
        if (!newWidget) {
            console.error('Invalid widget data');
            submitLoading.value = false;
            return;
        }
        try {
            await updateDashboardWidget({
                dashboardId,
                widget: newWidget,
                widgetId,
                ...widgetLayout,
            });
            const dashboardStore = useDashboardStore();
            await dashboardStore.refreshSelectedDashboard(dashboardId);
            submitLoading.value = false;
        } catch (error) {
            submitLoading.value = false;
            console.error('Error updating widget:', error);
        }
    };

    const initEditWidgetForm = async function (dashboardId: string, widgetId: string) {
        mode.value = 'edit';
        const removeInvalidLevelFilter = removeInvalidLevelFilterForWidgetId.value === widgetId;
        removeInvalidLevelFilterForWidgetId.value = null;
        const widgetData = await getWidget(dashboardId, widgetId);
        if (widgetData) {
            originalWidgetData.value = widgetData;
            const widgetFormData = createWidgetFormDataFromDto(widgetData);
            if (isGraphWidget(widgetData)) {
                filter.loadFilter(widgetData.graphWidget?.filter ?? null);
                if (removeInvalidLevelFilter && filter.all?.levelFilterMatchesNothing) {
                    filter.removeInvalidLevelFilter();
                }
            }
            widget.value = widgetFormData;
            widgetLoading.value = false;
        }
    };

    const initCopyLibraryWidgetForm = async (libraryWidget: ILibraryWidget) => {
        mode.value = 'copyLibrary';
        originalWidgetData.value = libraryWidget;
        const widgetFormData = createWidgetFormDataFromDto(libraryWidget);
        if (isGraphWidget(libraryWidget)) {
            filter.loadFilter(libraryWidget.graphWidget?.filter ?? null);
        }
        widget.value = widgetFormData;
        widgetLoading.value = false;
    };

    const initNewWidgetForm = () => {
        mode.value = 'create';
        filter.resetFilter();
    };

    const getWidget = async function (dashboardId: string, widgetId: string) {
        try {
            widgetLoading.value = true;
            const res = await getDashboardWidget({ widgetId, dashboardId });
            return res;
        } catch (error) {
            widgetLoading.value = false;
            console.error('Error fetching widget:', error);
        }
        return null;
    };

    const resetWidget = () => {
        widget.value = { ...defaultWidget };
        originalWidgetData.value = null;
    };

    return {
        isNewWidget,
        formIsValid,
        fetchKpiOtions,
        kpi,
        setSelectedWidgetType,
        widget,
        updateWidget,
        fetchPreview,
        createNewWidget,
        getWidget,
        mode,
        removeInvalidLevelFilterForWidgetId,
        initEditWidgetForm,
        initNewWidgetForm,
        editWidget,
        resetWidget,
        formLoading,
        originalWidgetData,
        initCopyLibraryWidgetForm,
    };
});
